import { Layout } from './Layout';
import { BackLink } from './BackLink';
import { PageSpinner } from './Spinner';
import { navigate } from '../lib/router';
import { Site, siteStatusBadge } from '../lib/site';

export type SiteTab = 'build' | 'materials' | 'backups' | 'versions' | 'domain' | 'billing' | 'settings';

const TABS: { key: SiteTab; label: string; href: (id: string) => string }[] = [
  { key: 'build',     label: 'Build',    href: (id) => `/sites/${id}` },
  { key: 'materials', label: 'Files',    href: (id) => `/sites/${id}/materials` },
  { key: 'backups',   label: 'Backups',  href: (id) => `/sites/${id}/backups` },
  { key: 'versions',  label: 'Versions', href: (id) => `/sites/${id}/versions` },
  { key: 'domain',    label: 'Domain',   href: (id) => `/sites/${id}/domain` },
  { key: 'billing',   label: 'Billing',  href: (id) => `/sites/${id}/billing` },
  { key: 'settings',  label: 'Settings', href: (id) => `/sites/${id}/settings` },
];

/** Wraps a site sub-page with the back-link, site header (name/status/external
 * links) and the tab strip. Renders a loading spinner until the site loads. */
export function SiteShell({
  site,
  loading,
  active,
  children,
}: {
  site: Site | null;
  loading: boolean;
  active: SiteTab;
  children?: any;
}) {
  if (loading && !site) {
    return <Layout active="sites"><PageSpinner label="Loading site…" /></Layout>;
  }
  if (!site) {
    return (
      <Layout active="sites">
        <div class="text-center py-16">
          <p class="text-slate-300 font-medium">Site not found</p>
          <a
            href="/sites"
            onClick={(e: MouseEvent) => { e.preventDefault(); navigate('/sites'); }}
            class="text-cyan-400 hover:text-cyan-300 text-sm mt-2 inline-block"
          >Back to sites</a>
        </div>
      </Layout>
    );
  }

  const badge = siteStatusBadge(site);
  const isBuilt = !!(site.buildType && site.buildType.length > 0);

  return (
    <Layout active="sites">
      <BackLink to="/sites" label="Back to sites" />
      <div class="mb-6">
        <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <h1 class="text-2xl font-bold text-slate-100">{site.name}</h1>
            <div class="flex items-center gap-2 mt-1">
              <span class={'text-xs px-2 py-0.5 rounded ' + badge.cls}>{badge.label}</span>
              <span class="text-sm text-slate-400">{site.slug}</span>
            </div>
          </div>
          <div class="flex items-center gap-2">
            {isBuilt && site.previewUrl && (
              <a
                href={site.previewUrl}
                target="_blank"
                rel="noopener"
                class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-600/50 text-slate-300 hover:bg-dark-700 transition-colors"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
                Preview
              </a>
            )}
            {site.publishedAt && site.url && (
              <a
                href={site.url}
                target="_blank"
                rel="noopener"
                class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 transition-colors"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/>
                </svg>
                View live
              </a>
            )}
          </div>
        </div>
      </div>
      <nav class="flex gap-1 border-b border-slate-700/50 mb-6 overflow-x-auto">
        {TABS.map((t) => {
          const href = t.href(site.id);
          return (
            <a
              href={href}
              onClick={(e: MouseEvent) => { e.preventDefault(); navigate(href); }}
              class={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                active === t.key ? 'border-cyan-400 text-cyan-400' : 'border-transparent text-slate-400 hover:text-slate-200'
              }`}
            >{t.label}</a>
          );
        })}
      </nav>
      {children}
    </Layout>
  );
}
